import { View } from '.';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { RolModuleEnum, RolModulePermissionEnum } from '@core/enums';
import { inject, signal, WritableSignal } from '@angular/core';
import { AuthService } from '@core/services';
import { InputTypeEnum } from '@shared/components/input/enums';
import { selectAuthenticationIsReseller, selectAuthenticationIsSuperUser } from '@store/selectors';
import { combineLatestWith, take } from 'rxjs';
import { AuditoriaForm } from '@core/models';
import { AUDIT_CREATE, AUDIT_UPDATE_FORM } from '@store/actions';

export const RolCombined = {
  ...RolModuleEnum,
  ...RolModulePermissionEnum
} as const;

export type RolCombinedType = typeof RolCombined[keyof typeof RolCombined];

export abstract class AbstractView<T> implements View {
  protected className: string;
  tabId?: string;
  RolCombined = RolCombined;
  authService = inject(AuthService);
  InputTypeEnum = InputTypeEnum;
  isChildrenComponent = false;
  isSuperUser$: WritableSignal<boolean> = signal(false);
  isReseller$: WritableSignal<boolean> = signal(false);

  constructor(
    public service: MessageService,
    public router: Router,
    public store: Store,
    public presenter: T,
  ) {
    this.className = this.constructor.name;

    this.store.select(selectAuthenticationIsSuperUser)
      .pipe(
        combineLatestWith(this.store.select(selectAuthenticationIsReseller)),
        take(1)
      ).subscribe(([isSuperUser, isReseller]) => {
        this.isSuperUser$.set(isSuperUser);
        this.isReseller$.set(isReseller);
      });
  }

  public getClassName(): string {
    return this.className;
  }

  showMessage(message: string): void {
    this.showWarn('Advertencia', message);
  }

  showInfo(title: string, detail: string): void {
    this.showToast('info', title, detail);
  }

  showSuccess(title: string, detail: string): void {
    this.showToast('success', title, detail);
  }

  showWarn(title: string, detail: string): void {
    this.showToast('warn', title, detail);
  }
  
  showError(title: string, detail: string): void {
    this.showToast('error', title, detail);
  }

  showToast(type: string, title: string, detail: string): void {
    this.service.add({
      severity: type,
      summary: title,
      detail: detail,
    });
  }

  redirectTo(ruta: string): void {
    this.router.navigate([ruta]);
  }

  getTabId(): string|undefined {
    return this.tabId;
  }

  setTabId(tabId?: string): void {
    this.tabId = tabId;
  }

  setIsChildrenComponent(isChildrenComponent: boolean): void {
    this.isChildrenComponent = isChildrenComponent;
  }

  hasRole(code: string): boolean {
    return this.authService.hasRole(code);
  }

  hasAnyRole(codes: string[]): boolean {
    return this.authService.hasAnyRole(codes);
  }

  generateAudit(auditForm: AuditoriaForm): void {
    this.store.dispatch(AUDIT_UPDATE_FORM({ auditForm }));
    this.store.dispatch(AUDIT_CREATE());
  }
}
